import React, { useMemo, useState } from 'react';
import {
  Link,
  Route,
  Switch,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import { Button, Card, CardBody, CardHeader, CardTitle } from 'reactstrap';
import { faCamera, faSpiderWeb } from '@fortawesome/pro-light-svg-icons';
import { Icon, IconStack, LoadingMessage } from 'js/components';
import OrgSnapshotSetItem from './OrgSnapshotSetItem';
import OrgSnapshotSetLayout from './OrgSnapshotSetLayout';
import OrgSnapshotCompareRadar from './OrgSnapshotCompareRadar';
import OrgSnapshotTrends from './OrgSnapshotTrends';
import {
  assessmentTemplateFromCompareDocument,
  snapshotCreatePath,
  snapshotReportPath,
  snapshotSourceName,
} from './org-snapshot-utils';
import styles from './OrgSnapshots.module.scss';

function SnapshotsEmpty({ showArchived, hasArchived }) {
  return (
    <div className="media text-muted mt-4 mb-4">
      <IconStack size="4x" fixedWidth className="mr-3">
        <Icon icon={faCamera} />
        <Icon icon={faSpiderWeb} transform="shrink-12 down-1" />
      </IconStack>
      <div className="media-body">
        {showArchived ? (
          <p>You don&apos;t have any snapshots to show.</p>
        ) : (
          <>
            <p>
              You don&apos;t have any snapshots to show.{' '}
              {hasArchived ? '(You have some archived ones though.)' : null}
            </p>
            <p>
              Create a snapshot to freeze this assessment&apos;s combined scores
              at a point in time.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function OrgSnapshotSetList({
  snapshots = [],
  archivedSnapshots = [],
  isLoading = false,
  createPath,
}) {
  const { workspaceId, orgId, sourceType, sourceKey } = useParams();
  const [showArchived, setShowArchived] = useState(false);
  const decodedKey = useMemo(() => decodeURIComponent(sourceKey), [sourceKey]);

  const snapshotRows = useMemo(() => {
    if (!showArchived) {
      return snapshots;
    }
    return [...snapshots, ...archivedSnapshots].sort((a, b) => {
      const aTime = new Date(a.snapshot_at || a.inserted_at || 0).getTime();
      const bTime = new Date(b.snapshot_at || b.inserted_at || 0).getTime();
      return bTime - aTime;
    });
  }, [archivedSnapshots, showArchived, snapshots]);

  return (
    <Card>
      <CardHeader>
        <div className="action-items">
          <Link className="btn btn-primary create-link" to={createPath}>
            <Icon icon="plus" className="btn-icon-left" /> Create
          </Link>
          <Button
            className="ml-2"
            onClick={() => setShowArchived((value) => !value)}
          >
            {showArchived ? 'Hide archived' : 'Show archived'}
          </Button>
        </div>
        <CardTitle tag="h5">Snapshots</CardTitle>
      </CardHeader>
      <CardBody>
        {isLoading && snapshotRows.length === 0 ? (
          <LoadingMessage>Loading snapshots…</LoadingMessage>
        ) : snapshotRows.length === 0 ? (
          <SnapshotsEmpty
            showArchived={showArchived}
            hasArchived={archivedSnapshots.length > 0}
          />
        ) : (
          <ul className={styles.snapshotList}>
            {snapshotRows.map((snapshot) => (
              <OrgSnapshotSetItem
                key={snapshot.id}
                snapshot={snapshot}
                to={snapshotReportPath(
                  workspaceId,
                  orgId,
                  sourceType,
                  decodedKey,
                  snapshot.id
                )}
              />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function OrgSnapshotSetDetail({
  organization,
  workspace,
  assessmentHistorySummaries = [],
  snapshots = [],
  archivedSnapshots = [],
  compareDocument = null,
  compareSnapshots = [],
  isLoading = false,
  isCompareLoading = false,
  compareError = null,
}) {
  const { path } = useRouteMatch();
  const { workspaceId, orgId, sourceType, sourceKey } = useParams();
  const decodedKey = useMemo(() => decodeURIComponent(sourceKey), [sourceKey]);
  const createPath = snapshotCreatePath(
    workspaceId,
    orgId,
    sourceType,
    decodedKey
  );
  const sourceName = snapshotSourceName({
    sourceKey: decodedKey,
    snapshots,
    summaries: assessmentHistorySummaries,
  });
  const assessmentTemplate = useMemo(
    () =>
      assessmentTemplateFromCompareDocument({
        sourceKey: decodedKey,
        sourceName,
        document: compareDocument,
      }),
    [compareDocument, decodedKey, sourceName]
  );

  const chart = (
    <OrgSnapshotCompareRadar
      organization={organization}
      assessmentTemplate={assessmentTemplate}
      sourceDocument={compareDocument}
      snapshots={compareSnapshots}
      isLoading={isCompareLoading}
      error={compareError}
      createPath={createPath}
    />
  );

  return (
    <OrgSnapshotSetLayout
      organization={organization}
      workspace={workspace}
      assessmentHistorySummaries={assessmentHistorySummaries}
      chart={chart}
    >
      <Switch>
        <Route exact path={`${path}/trends/:subOrgId?`}>
          <OrgSnapshotTrends
            organization={organization}
            workspace={workspace}
            assessmentTemplate={assessmentTemplate}
            compareDocument={compareDocument}
            compareSnapshots={compareSnapshots}
            isCompareLoading={isCompareLoading}
            compareError={compareError}
            createPath={createPath}
          />
        </Route>
        <Route exact path={path}>
          <OrgSnapshotSetList
            snapshots={snapshots}
            archivedSnapshots={archivedSnapshots}
            isLoading={isLoading}
            createPath={createPath}
          />
        </Route>
      </Switch>
    </OrgSnapshotSetLayout>
  );
}

export default OrgSnapshotSetDetail;
